/**
 * THE SHARED LOOK OF A GENERATED DOCUMENT — the letterhead, the section bands and
 * the dates every FlightDeck PDF wears (design rule R18, approved 3 Oct 2026).
 *
 * WHY IT IS HERE AND NOT COPIED. Five documents are minted with pdf-lib —
 * the authority to pay, the requirements document, the charter, the change request
 * and the steering pack — and each had grown its own title row and its own solid
 * teal band. Vinnie, 3 Oct: the printed documents should look like one serious tool,
 * the way the on-screen forms now do (R17). So the identity lives in one place:
 *
 *   • a LETTERHEAD — a small teal eyebrow (what kind of document), a large subject
 *     (its name), a quiet meta line (project · when · status), the workspace mark
 *     at the top-right, and a teal hairline under the lot;
 *   • TINTED section bands — a pale teal fill with a teal left tab and teal text,
 *     never a solid block of colour with white text;
 *   • WORDED DATES — "3 Oct 2026", not "03/10/2026", everywhere a person reads one.
 *
 * It draws only what it is handed (fonts, geometry, an already-embedded logo), so a
 * document stays a pure function of its content and the same facts make the same
 * bytes — which the change request and the authority to pay rely on to fingerprint.
 */
import { rgb, type PDFFont, type PDFPage, type PDFImage } from 'pdf-lib';
import { MONTHS } from './when';

/* A printed page is light, whatever the reader's screen theme. These are the light
   palette's own tokens, shared so every document inks them identically. */
export const INK = rgb(0.1, 0.13, 0.16);
export const QUIET = rgb(0.42, 0.46, 0.5);
export const RULE = rgb(0.8, 0.83, 0.85);
/** FlightDeck's teal — the light palette's --brand. */
export const BRAND = rgb(0.043, 0.431, 0.467);
/** The band fill: the brand at about a tenth, over white. Calm, not a block. */
export const TINT = rgb(0.906, 0.945, 0.949);
/** A table's label cells — a touch stronger than a band, so a label still reads. */
export const TINT_LABEL = rgb(0.886, 0.929, 0.933);
/** The panel a seal or a sunk note sits in. */
export const SUNK = rgb(0.965, 0.972, 0.976);

/** A timestamp as a worded day-first date — "3 Oct 2026". */
export function wordedDate(at: number): string {
  const d = new Date(at);
  if (!Number.isFinite(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * A date a caller already formatted, re-worded for display.
 *
 * The documents are handed dates as strings their callers built, and some build
 * "03/10/2026" — which the authority to pay also parses back to stamp the file's
 * date deterministically. So the slashed form stays the one that is passed and
 * stored; this only changes how it READS, turning "03/10/2026" into "3 Oct 2026".
 * A string that is not day-first-slashed is handed back untouched (it is already
 * worded, or is something this should not second-guess).
 */
export function wordedFromSlashed(s: string): string {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((s ?? '').trim());
  if (!m) return s;
  const [, d, mo, y] = m;
  const mi = Number(mo) - 1;
  if (mi < 0 || mi > 11) return s;
  return `${Number(d)} ${MONTHS[mi]} ${y}`;
}

/**
 * The "who did it, and when" part of a letterhead's meta line. Every produced
 * document names the signed-in person who produced it, just before the date, in
 * the document's own verb: "Prepared by Marcia Vaughn · 13 Oct 2026" on a charter,
 * "Raised by Marcia Vaughn · 13 Oct 2026" on a change request. So a printed
 * document says, on its own face, who stands behind it. Where no person is known
 * the by-line is dropped and the date keeps the verb, so the line still reads
 * ("Prepared 13 Oct 2026"). Returns the one or two segments to splice into the
 * document's meta line, before its status.
 */
export function preparedSegments(by: string | undefined, on: string, verb = 'Prepared'): string[] {
  const when = wordedFromSlashed(on);
  const who = (by ?? '').trim();
  return who ? [`${verb} by ${who}`, when] : [`${verb} ${when}`];
}

/**
 * A document's revision, named only once it has one. A document is produced at
 * its first issue (version 1), and that carries no revision line: a bare
 * "Version 1" is noise, and beside a product name ("Loan Hub 1.4.0 · Version 1")
 * it reads as the product's version rather than the document's. Once a signed
 * document is superseded and re-issued, the new issue says "Revision 2",
 * "Revision 3" — document-control language a reader cannot mistake for a release
 * number. Returns the label, or null at the first issue (so a caller drops it
 * with .filter(Boolean)). Vinnie, 14 Oct 2026.
 */
export function revisionLabel(version: string | number): string | null {
  const n = Number(version);
  return Number.isFinite(n) && n > 1 ? `Revision ${n}` : null;
}

export interface Letterhead {
  /** The small teal line above the title — the KIND of document, upper-cased here. */
  eyebrow: string;
  /** The large subject — the document's name. */
  subject: string;
  /** The quiet line under it — "project · when · status", already assembled. */
  meta?: string;
  /** The workspace mark, already embedded, drawn top-right and a little bigger (R18). */
  logo?: PDFImage;
}

/**
 * Wrap a line to at most `max` lines that each fit `avail` at `size`. A single word
 * wider than the line overhangs rather than vanishing, and if the text needs more
 * than `max` lines the overflow is folded into the last one — the subject has
 * already been shrunk toward a floor before this is reached, so this only catches
 * the genuinely long title.
 */
function fitLines(text: string, font: PDFFont, size: number, avail: number, max: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [text];
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const tried = line ? `${line} ${w}` : w;
    if (!line || font.widthOfTextAtSize(tried, size) <= avail) line = tried;
    else {
      lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  if (lines.length > max) {
    const head = lines.slice(0, max - 1);
    head.push(lines.slice(max - 1).join(' '));
    return head;
  }
  return lines;
}

/**
 * Draw the letterhead at the top of a page and return the y just below its hairline,
 * which is where the body starts. `top` is the page's top text line (height − margin).
 */
export function drawLetterhead(
  page: PDFPage,
  geo: { x: number; width: number; top: number },
  fonts: { regular: PDFFont; bold: PDFFont; title?: PDFFont },
  content: Letterhead,
): number {
  const { x, width, top } = geo;
  const { regular, bold } = fonts;
  // The subject wears the heavy title face (Inter Black) where one is supplied,
  // falling back to bold so a caller that has not embedded it still reads right.
  const titleFace = fonts.title ?? bold;

  // The workspace mark, top-right, a little bigger than it was (decision 4). Its
  // width also bounds the subject below, so a long title never runs under it.
  let logoW = 0;
  if (content.logo) {
    const tall = 26;
    const scale = Math.min(tall / content.logo.height, (width * 0.4) / content.logo.width);
    logoW = content.logo.width * scale;
    const h = content.logo.height * scale;
    page.drawImage(content.logo, { x: x + width - logoW, y: top - h + 2, width: logoW, height: h });
  }

  // Eyebrow — teal, upper-case. Drawn as one string so it stays machine-readable
  // (a legal document must be searchable); the small-caps look carries the identity.
  page.drawText(content.eyebrow.toUpperCase(), { x, y: top - 2, size: 8, font: bold, color: BRAND });

  // Subject — the big name of the document, FIT to the space: shrink from 23 toward
  // a floor so a long title (or a long board/project name) never runs off the page
  // or under the logo; if it still will not fit at the floor, wrap to a second line.
  const avail = width - (logoW ? logoW + 16 : 0);
  let subjectSize = 23;
  while (subjectSize > 15 && titleFace.widthOfTextAtSize(content.subject, subjectSize) > avail) subjectSize -= 0.5;
  const subjectLines =
    titleFace.widthOfTextAtSize(content.subject, subjectSize) > avail
      ? fitLines(content.subject, titleFace, subjectSize, avail, 2)
      : [content.subject];
  let y = top - 2 - 16 - subjectSize * 0.72;
  subjectLines.forEach((line, i) => {
    if (i > 0) y -= subjectSize * 1.12;
    page.drawText(line, { x, y, size: subjectSize, font: titleFace, color: INK });
  });

  // Meta — quiet, one line.
  if (content.meta && content.meta.trim()) {
    y -= 18;
    page.drawText(content.meta, { x, y, size: 10.5, font: regular, color: QUIET });
  }

  // The teal hairline that closes the block.
  y -= 16;
  page.drawLine({ start: { x, y }, end: { x: x + width, y }, thickness: 1, color: BRAND });
  return y - 18; // the body starts a little below the rule
}

/**
 * Draw a tinted section band and return its height. `yBottom` is the band's bottom
 * edge (pdf-lib draws rectangles up from there). A teal left tab, a teal number
 * where there is one, and the teal title — never a solid block with white text.
 */
export function drawBand(
  page: PDFPage,
  o: { x: number; yBottom: number; width: number; title: string; number?: number | string; bold: PDFFont },
): number {
  const height = 26;
  page.drawRectangle({ x: o.x, y: o.yBottom, width: o.width, height, color: TINT });
  // The left tab — a short teal bar, the band's own accent.
  page.drawRectangle({ x: o.x, y: o.yBottom, width: 3, height, color: BRAND });
  const textY = o.yBottom + (height - 11) / 2 + 1.5;
  let tx = o.x + 14;
  if (o.number !== undefined && o.number !== '') {
    const num = String(o.number);
    page.drawText(num, { x: tx, y: textY, size: 11, font: o.bold, color: BRAND });
    tx += o.bold.widthOfTextAtSize(num, 11) + 10;
  }
  page.drawText(o.title, { x: tx, y: textY, size: 11, font: o.bold, color: BRAND });
  return height;
}
